import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Loader2, RefreshCw } from 'lucide-react';
import { adsApi, campaignsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import {
  normalizeSpCampaign,
  campaignAdProductLabel,
} from '@/types/spCampaigns';
import {
  campaignBiddingStrategyCopy,
  campaignBiddingStrategyFromRaw,
  spCampaignTargetingTypeLabel,
} from '@/utils/spAdGroupCreate';
import { HourlyBudgetEditor } from '@/components/hourlyBudget/HourlyBudgetEditor';
import { AutoBiddingActivityPanel } from '@/components/AutoBiddingActivityPanel';
import { CampaignKeywordRecommendationsSection } from '@/components/CampaignKeywordRecommendationsSection';
import { CampaignPerformanceSection } from '@/components/performance/CampaignPerformanceSection';
import {
  CampaignDetailsSectionPanel,
  parseCampaignDetailsSection,
  type CampaignDetailsSectionId,
} from '@/components/CampaignDetailsSectionPanel';
import {
  defaultAdGroupSection,
  parseAdGroupSection,
  type AdGroupSectionId,
} from '@/components/CampaignAdGroupContent';
import { TableSearchInput } from '@/components/TableSearchInput';
import { BudgetUsageEntityPanel } from '@/components/dashboard/BudgetUsageEntityPanel';
import { EditableCampaignBudgetCell } from '@/components/EditableCampaignBudgetCell';
import {
  resolveEditableCampaignBudget,
} from '@/api/campaignBudgetUpdate';
import { Badge, entityStateBadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { downloadScheduledRunHistoryCsv } from '@/utils/scheduledRunHistoryCsv';
import { filterRowsByTableSearch } from '@/utils/tableSearch';

function parseProductParam(raw: string | null): CampaignAdProduct {
  if (raw === 'SB' || raw === 'SD') return raw;
  return 'SP';
}

const HOURLY_BUDGET_AD_PRODUCTS: CampaignAdProduct[] = ['SP', 'SB', 'SD'];

function supportsHourlyBudget(product: CampaignAdProduct): boolean {
  return HOURLY_BUDGET_AD_PRODUCTS.includes(product);
}

function CampaignSyncNowButton({
  isPending,
  errorMessage,
  onClick,
}: {
  isPending: boolean;
  errorMessage?: string | null;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col items-end gap-1 ml-auto shrink-0">
      {errorMessage ? (
        <span className="text-xs text-error max-w-[16rem] text-right" title={errorMessage}>
          {errorMessage}
        </span>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={isPending}
        onClick={onClick}
        className="whitespace-nowrap"
      >
        {isPending ? (
          <>
            <Spinner className="h-3.5 w-3.5" />
            Syncing…
          </>
        ) : (
          <>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Sync now
          </>
        )}
      </Button>
    </div>
  );
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatRunLogTime(iso: unknown): string {
  if (iso == null || typeof iso !== 'string') return 'N/A';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

type SpAdGroupRow = {
  ad_group_id?: string;
  campaign_id?: string;
  name?: string;
  state?: string;
  default_bid?: number;
  extended_data?: {
    serving_status?: string;
    status_reasons?: string[];
  } | null;
};

function parseSpAdGroupsPayload(data: unknown): { adGroups: SpAdGroupRow[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { adGroups: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const adGroups = Array.isArray(raw.ad_groups) ? (raw.ad_groups as SpAdGroupRow[]) : [];
  const totalResults =
    typeof raw.total_results === 'number' ? raw.total_results : adGroups.length;
  return { adGroups, totalResults };
}

export function SpCampaignDetailsPage() {
  const { profileId: profileIdParam, campaignId: campaignIdParam } = useParams<{
    profileId: string;
    campaignId: string;
  }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const campaignId = campaignIdParam ? decodeURIComponent(campaignIdParam) : '';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adProductFromUrl = parseProductParam(searchParams.get('product'));
  const detailsSection = parseCampaignDetailsSection(searchParams.get('section'));
  const selectedAdGroupId = searchParams.get('adGroup') || null;

  const goBack = () => {
    navigate(-1);
  };

  const setDetailsSection = (section: CampaignDetailsSectionId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (section === 'ad-groups') {
          next.delete('section');
        } else {
          next.set('section', section);
        }
        next.delete('adGroup');
        next.delete('adGroupSection');
        return next;
      },
      { replace: true }
    );
  };

  // Entering an ad group is a real navigation (a Link), so browser Back returns to the list.
  const adGroupHref = (adGroupId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete('section');
    next.delete('adGroupSection');
    next.set('adGroup', adGroupId);
    return `?${next.toString()}`;
  };

  const backToAdGroups = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('section');
      next.delete('adGroup');
      next.delete('adGroupSection');
      return next;
    });
  };

  const setAdGroupSection = (section: AdGroupSectionId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (section === defaultAdGroupSection(adProduct)) {
          next.delete('adGroupSection');
        } else {
          next.set('adGroupSection', section);
        }
        return next;
      },
      { replace: true }
    );
  };

  // Fetch only this campaign; the key stays under 'profile-campaigns' so list invalidations refresh it.
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile-campaigns', profileId, 'campaign', campaignId, adProductFromUrl],
    queryFn: async () => {
      const res = await adsApi.getProfileCampaign(profileId, campaignId, adProductFromUrl);
      if (!res.success) throw new Error('Failed to load campaign');
      return res.data;
    },
    enabled: !!profileId && !!campaignId,
    retry: (failureCount, err) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      return status !== 404 && status !== 401 && failureCount < 3;
    },
  });

  const campaign = useMemo(
    () => (data?.campaign ? normalizeSpCampaign(data.campaign) : undefined),
    [data]
  );

  const refreshCampaignAfterMutation = useCallback(() => {
    void qc.invalidateQueries({
      queryKey: ['profile-campaigns', profileId, 'campaign', campaignId, adProductFromUrl],
    });
    void qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
    void qc.invalidateQueries({ queryKey: ['workspace-entity-page', 'campaigns', profileId] });
  }, [adProductFromUrl, campaignId, profileId, qc]);

  const adProduct: CampaignAdProduct = campaign?.adProduct ?? adProductFromUrl;
  const isKeywordCampaign = adProduct === 'SP' || adProduct === 'SB';
  const adGroupSection = parseAdGroupSection(
    searchParams.get('adGroupSection'),
    adProduct
  );

  const [runHistorySearchQuery, setRunHistorySearchQuery] = useState('');
  const [isDownloadingRunHistoryCsv, setIsDownloadingRunHistoryCsv] = useState(false);

  const {
    data: hourlyRunLogs,
    isLoading: isHourlyRunLogsLoading,
    error: hourlyRunLogsError,
  } = useQuery({
    queryKey: ['campaign-hourly-budget-run-logs', profileId, campaignId],
    queryFn: async () => {
      const res = await campaignsApi.getHourlyBudgetRunLogs(profileId, campaignId, {
        limit: 50,
        product: adProductFromUrl,
      });
      if (!res.success) throw new Error('Failed to load hourly budget run logs');
      const raw = res.data;
      if (!Array.isArray(raw)) return [] as Record<string, unknown>[];
      return raw.filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object');
    },
    enabled: !!profileId && !!campaignId && supportsHourlyBudget(adProductFromUrl),
  });

  const {
    data: spAdGroupsData,
    isLoading: isSpAdGroupsLoading,
    error: spAdGroupsError,
    refetch: refetchSpAdGroups,
  } = useQuery({
    queryKey: [`${adProduct.toLowerCase()}-ad-groups`, profileId, campaignId],
    queryFn: async () => {
      if (adProduct === 'SD') {
        // The SD endpoint lists every ad group on the profile, so narrow it to this campaign.
        // SD IDs come back as numbers from the stored Amazon payload; the URL holds strings.
        const res = await adsApi.getSdAdGroups(profileId);
        if (!res.success) throw new Error('Failed to load ad groups');
        const all = (res.data.ad_groups ?? []) as SpAdGroupRow[];
        const adGroups = all
          .filter((row) => String(row.campaign_id) === campaignId)
          .map((row) => ({ ...row, ad_group_id: String(row.ad_group_id) }));
        return { ad_groups: adGroups, total_results: adGroups.length };
      }
      const body = {
        include_extended_data_fields: true,
        campaign_ids: [campaignId],
        ad_group_ids: [],
        states: ['ENABLED', 'PAUSED', 'ARCHIVED'] as Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>,
        max_results: 500,
      };
      const res =
        adProduct === 'SB'
          ? await adsApi.listSbAdGroups(profileId, body)
          : await adsApi.listSpAdGroups(profileId, body);
      if (!res.success) throw new Error('Failed to load ad groups');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!campaign,
  });

  const spAdGroupsPayload = useMemo(() => parseSpAdGroupsPayload(spAdGroupsData), [spAdGroupsData]);
  const adGroupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const adGroup of spAdGroupsPayload.adGroups) {
      if (adGroup.ad_group_id) {
        map.set(adGroup.ad_group_id, adGroup.name || adGroup.ad_group_id);
      }
    }
    return map;
  }, [spAdGroupsPayload.adGroups]);

  const invalidateCampaignCatalog = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] }),
      qc.invalidateQueries({ queryKey: ['sp-ad-groups', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['sd-ad-groups', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['sp-product-ads', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['sd-product-ads', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['sd-creatives', profileId, campaignId] }),
      qc.invalidateQueries({ queryKey: ['campaign-ad-group-targets'] }),
      qc.invalidateQueries({ queryKey: ['campaign-keywords-catalog'] }),
      qc.invalidateQueries({ queryKey: ['campaign-sp-targets', profileId, campaignId] }),
      qc.invalidateQueries({
        queryKey: ['campaign-details-section-negative-keywords', profileId, campaignId],
      }),
      qc.invalidateQueries({ queryKey: ['campaign-negative-keywords', profileId, campaignId] }),
    ]);
  }, [qc, profileId, campaignId]);

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.syncCampaignCatalog(profileId, campaignId, adProductFromUrl);
      if (!res.success) throw new Error('Sync failed');
      return res.data;
    },
    onSuccess: async () => {
      await invalidateCampaignCatalog();
    },
  });
  const syncErrorMessage = syncMutation.isError
    ? (syncMutation.error as Error).message || 'Sync failed'
    : null;

  const visibleRunLogs = useMemo(() => {
    const logs = hourlyRunLogs ?? [];
    return filterRowsByTableSearch(logs, runHistorySearchQuery, (row) => [
      formatRunLogTime(row.run_time),
      formatRunLogTime(row.evaluated_ist_hour),
      row.status,
      row.multiplier_applied,
      row.effective_increment,
      row.message,
      row.old_budget,
      row.new_budget,
    ]);
  }, [hourlyRunLogs, runHistorySearchQuery]);

  const handleDownloadRunHistoryCsv = useCallback(async () => {
    const logs = hourlyRunLogs ?? [];
    if (logs.length === 0 || isDownloadingRunHistoryCsv) return;
    setIsDownloadingRunHistoryCsv(true);
    try {
      await Promise.resolve();
      downloadScheduledRunHistoryCsv(logs);
    } finally {
      setIsDownloadingRunHistoryCsv(false);
    }
  }, [hourlyRunLogs, isDownloadingRunHistoryCsv]);

  if (!profileId || !campaignId) {
    return <div className="p-8">Missing route parameters.</div>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-brand" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="max-w-xl mx-auto p-8">
        <p className="text-error mb-4">Campaign not found or failed to load.</p>
        <p className="text-sm text-foreground-subtle mb-4">
          If you just created this campaign, sync it from Amazon to store it in the database.
        </p>
        <div className="flex items-center gap-4">
          <button type="button" onClick={goBack} className="text-brand underline">
            Back to campaigns
          </button>
          <CampaignSyncNowButton
            isPending={syncMutation.isPending}
            errorMessage={syncErrorMessage}
            onClick={() => syncMutation.mutate()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border">
        <div className="pl-16 pr-4 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="p-2 rounded-xl hover:bg-surface-muted"
            aria-label="Back to campaigns"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-foreground">{campaign.name}</h1>
          <Badge variant={entityStateBadgeVariant(campaign.state)}>{campaign.state}</Badge>
          <CampaignSyncNowButton
            isPending={syncMutation.isPending}
            errorMessage={syncErrorMessage}
            onClick={() => syncMutation.mutate()}
          />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="bg-surface rounded-xl shadow border border-border p-4">
          <div
            className={cn(
              'grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm',
              campaign.adProduct === 'SP' ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
            )}
          >
            <div>
              <div className="text-foreground-subtle">Ad product</div>
              <div>{campaignAdProductLabel(campaign.adProduct)}</div>
            </div>
            <div>
              <div className="text-foreground-subtle">Budget</div>
              <div>
                <EditableCampaignBudgetCell
                  profileId={profileId}
                  variant="inline"
                  displayValue={`${campaign.budget} (${campaign.budgetType})`}
                  editableTarget={resolveEditableCampaignBudget(campaign)}
                  campaignLabel={campaign.name}
                  onUpdated={refreshCampaignAfterMutation}
                />
              </div>
            </div>
            <div>
              <div className="text-foreground-subtle">Targeting</div>
              <div>{spCampaignTargetingTypeLabel(campaign)}</div>
            </div>
            {campaign.adProduct === 'SP' ? (
              <div>
                <div className="text-foreground-subtle">Bidding strategy</div>
                <div>
                  {campaignBiddingStrategyCopy(campaignBiddingStrategyFromRaw(campaign.raw)).label}
                </div>
              </div>
            ) : null}
            <div>
              <div className="text-foreground-subtle">Dates</div>
              <div>
                {campaign.startDate || 'N/A'}
                {' – '}
                {campaign.endDate || 'N/A'}
              </div>
            </div>
            <BudgetUsageEntityPanel
              profileId={profileId}
              campaignId={campaign.campaignId}
              entityLabel={campaign.name}
              display="todayLatest"
            />
          </div>
        </div>

        <CampaignPerformanceSection
          profileId={profileId}
          campaignId={campaignId}
          campaignName={campaign.name}
        />

        <CampaignDetailsSectionPanel
          profileId={profileId}
          campaignId={campaignId}
          campaign={campaign}
          adProduct={adProduct}
          section={detailsSection}
          onSectionChange={setDetailsSection}
          adGroups={spAdGroupsPayload.adGroups}
          adGroupsTotal={spAdGroupsPayload.totalResults}
          adGroupsLoading={isSpAdGroupsLoading}
          adGroupsError={spAdGroupsError as Error | null}
          onRetryAdGroups={() => void refetchSpAdGroups()}
          adGroupNameById={adGroupNameById}
          selectedAdGroupId={selectedAdGroupId}
          adGroupSection={adGroupSection}
          onAdGroupSectionChange={setAdGroupSection}
          adGroupHref={adGroupHref}
          onBackToAdGroups={backToAdGroups}
          onCampaignUpdated={refreshCampaignAfterMutation}
        />

        {supportsHourlyBudget(campaign.adProduct) && (
        <>
        <HourlyBudgetEditor
          profileId={profileId}
          campaignRef={campaignId}
          product={campaign.adProduct}
        />
        <div className="bg-surface rounded-xl shadow border border-border p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Scheduled run history</h3>
                <p className="text-xs text-foreground-subtle mt-0.5">
                  Latest updates from the hourly budget job (most recent first).
                </p>
              </div>
              {!isHourlyRunLogsLoading && !hourlyRunLogsError && (hourlyRunLogs?.length ?? 0) > 0 ? (
                <div className="flex items-center gap-2">
                  <TableSearchInput
                    value={runHistorySearchQuery}
                    onChange={setRunHistorySearchQuery}
                    placeholder="Search run history…"
                    id="campaign-run-history-search"
                  />
                  <button
                    type="button"
                    onClick={() => void handleDownloadRunHistoryCsv()}
                    disabled={isDownloadingRunHistoryCsv}
                    aria-busy={isDownloadingRunHistoryCsv}
                    aria-label={
                      isDownloadingRunHistoryCsv
                        ? 'Preparing CSV download'
                        : 'Download scheduled run history as CSV'
                    }
                    className="inline-flex h-[2.375rem] shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3 text-sm font-medium text-brand-foreground hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {isDownloadingRunHistoryCsv ? (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    )}
                    <span className="whitespace-nowrap">
                      {isDownloadingRunHistoryCsv ? 'Preparing…' : 'Download CSV'}
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
            {isHourlyRunLogsLoading ? (
              <div className="text-sm text-foreground-subtle">Loading run history...</div>
            ) : hourlyRunLogsError ? (
              <div className="text-sm text-error">
                {(hourlyRunLogsError as Error).message || 'Failed to load run history'}
              </div>
            ) : !hourlyRunLogs?.length ? (
              <div className="text-sm text-foreground-subtle">No runs recorded yet.</div>
            ) : visibleRunLogs.length === 0 ? (
              <div className="text-sm text-foreground-subtle">No rows match your search.</div>
            ) : (
              <>
                {runHistorySearchQuery.trim() ? (
                  <p className="text-sm text-foreground-subtle mb-2">
                    Showing {visibleRunLogs.length} of {hourlyRunLogs.length} run(s)
                  </p>
                ) : null}
              <div className="max-h-56 overflow-x-auto overflow-y-auto rounded-xl border border-border">
                <table className="min-w-full text-sm table-fixed">
                  <thead>
                    <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                      <th className="px-3 py-2 whitespace-nowrap">Run time</th>
                      <th className="px-3 py-2 whitespace-nowrap">IST hour</th>
                      <th className="px-3 py-2 whitespace-nowrap">Status</th>
                      <th className="px-3 py-2 whitespace-nowrap">Multiplier</th>
                      <th className="px-3 py-2 whitespace-nowrap">Increment</th>
                      <th className="px-3 py-2 w-[28%]">Message</th>
                      <th className="px-3 py-2 whitespace-nowrap">Prev. budget</th>
                      <th className="px-3 py-2 whitespace-nowrap">New budget</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {visibleRunLogs.map((row, i) => {
                      const st = String(row.status ?? '');
                      const stLower = st.toLowerCase();
                      const ok = stLower === 'success';
                      const skipped = stLower === 'skipped';
                      const rid = row.id != null ? String(row.id) : `log-${i}`;
                      return (
                        <tr key={rid} className="bg-surface hover:bg-page/80">
                          <td className="px-3 py-2 text-foreground whitespace-nowrap">
                            {formatRunLogTime(row.run_time)}
                          </td>
                          <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                            {formatRunLogTime(row.evaluated_ist_hour)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                ok
                                  ? 'bg-success-subtle text-success-ink'
                                  : skipped
                                    ? 'bg-warning-subtle text-warning-ink'
                                    : 'bg-error-subtle text-error-ink'
                              }`}
                            >
                              {st || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                            {displayValue(row.multiplier_applied)}
                          </td>
                          <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                            {displayValue(row.effective_increment)}
                          </td>
                          <td className="px-3 py-2 text-foreground-muted max-w-0">
                            <span
                              className="block truncate"
                              title={
                                row.message != null && row.message !== ''
                                  ? String(row.message)
                                  : undefined
                              }
                            >
                              {displayValue(row.message)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                            {displayValue(row.old_budget)}
                          </td>
                          <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                            {displayValue(row.new_budget)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
        </div>
        </>
        )}

        <AutoBiddingActivityPanel
          profileId={profileId}
          campaignId={campaignId}
          campaignName={campaign.name}
          adProduct={campaign.adProduct}
        />

        {isKeywordCampaign && profileId && campaignId && (
          <CampaignKeywordRecommendationsSection
            profileId={profileId}
            campaignId={campaignId}
            adProduct={adProduct}
            adGroupNameById={adGroupNameById}
          />
        )}
      </main>
    </div>
  );
}
